import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import Button from '@mui/material/Button';
import { useContext, useState } from 'react';
import { SessionContext } from '../../../Contexts/SessionContext';
import { SurveyViewModel } from '../../../Contexts/SessionState';
import TextField from '@mui/material/TextField';
import Grid from '@mui/material/Grid2';

interface IProps {
    open: boolean;
    onClose: () => void;
    references: string[];
    type: 'site' | 'survey';
    multiDeselection: boolean;
}

export default function DeselectionReasonDialog({ open, onClose, references, type }: IProps) {
    const { updateSessionState, sessionState } = useContext(SessionContext);
    const [value, setValue] = useState('');

    const lastReason = sessionState.lastReasonForRemoval || '';
    const referenceSet = new Set(references?.map(r => r?.trim()).filter(Boolean));

    const getRef = (site: SurveyViewModel) => site.surveyRef.split('|')[0];

    const onsubmit = (usePrevReason?: boolean) => {
        const selectedSites: SurveyViewModel[] = sessionState.selectedSitesAndSurveys || [];
        const reason = (usePrevReason ? lastReason : value).trim();

        if (!reason) return;
        if (!referenceSet.size) return;

        const updatedSites = selectedSites.map((site) => {
            const ref = getRef(site);
            if (!referenceSet.has(ref)) return site;

            if (type === 'site') {
                return {
                    ...site,
                    reasonForSiteRemoval: reason,
                    siteActive: false,
                    surveyActive: false,
                    reasonForSurveyRemoval: 'Removed in Site Selection',
                    manuallyRemoved: true
                };
            }

            return {
                ...site,
                reasonForSurveyRemoval: reason,
                reasonForSiteRemoval: site.reasonForSiteRemoval ?? 'Removed in Survey Selection',
                surveyActive: false,
                manuallyRemoved: true
            };
        });

        const success = selectedSites.some(site => referenceSet.has(getRef(site)));
        if (!success) return;

        updateSessionState({
            selectedSitesAndSurveys: updatedSites,
            lastReasonForRemoval: reason,
        });
        onClose();
    };

    return (
        <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
            <DialogTitle>Deselection</DialogTitle>
            <DialogContent>
                <Grid container spacing={2} sx={{ mt: 1 }}>
                    <Grid size={9}>
                        <TextField
                            fullWidth
                            label="New Reason"
                            value={value}
                            onChange={(e) => setValue(e.target.value)}
                        />
                    </Grid>
                    <Grid size={3} sx={{ display: 'flex', alignItems: 'stretch' }}>
                        <Button
                            fullWidth
                            disabled={value.trim().length === 0 || (references?.length ?? 0) === 0}
                            onClick={() => onsubmit()}
                        >
                            Confirm
                        </Button>
                    </Grid>

                    {lastReason.length > 0 && (
                        <>
                            <Grid size={9}>
                                <TextField
                                    fullWidth
                                    label="Previous Reason"
                                    value={lastReason}
                                    InputProps={{ readOnly: true }}
                                />
                            </Grid>
                            <Grid size={3} sx={{ display: 'flex', alignItems: 'stretch' }}>
                                <Button
                                    fullWidth
                                    onClick={() => onsubmit(true)}
                                    disabled={(references?.length ?? 0) === 0}
                                >
                                    Confirm
                                </Button>
                            </Grid>
                        </>
                    )}
                </Grid>
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose}>Cancel</Button>
            </DialogActions>
        </Dialog>
    );
}
